import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import {
  TogalProject,
  TogalSet,
  TogalClassification,
  TogalInvitedUserCard,
  TogalSetDetails,
} from "../../../../integrations/qto/sources/togal/types";
import type { AggregatedQto } from "../../../../integrations/qto/sources/togal/types";
import {
  fetchTogalProjects,
  fetchTogalSet,
  fetchTogalSets,
  togalValidateSession,
  aggregateClassificationQuantities,
} from "../../../../integrations/qto/sources/togal/api";
import { workbookCache } from "../../../../excel/workbook/cache";
import { useBanner } from "../../../context/BannerContext";
import { UnauthorizedError } from "../../../../errors";
import { TOGAL_LOAD_ERROR, TOGAL_REFRESHED, TOGAL_REFRESH_PARTIAL } from "../../../constants";
import { browserStore } from "../../../../utils/storageKeys";

function settleAll<T extends readonly unknown[]>(promises: { [K in keyof T]: Promise<T[K]> }): Promise<{
  [K in keyof T]: PromiseSettledResult<T[K]>;
}> {
  return Promise.all(
    promises.map((p) =>
      Promise.resolve(p).then(
        (value) => ({ status: "fulfilled" as const, value }),
        (reason) => ({ status: "rejected" as const, reason })
      )
    )
  ) as Promise<{ [K in keyof T]: PromiseSettledResult<T[K]> }>;
}

export type TogalState = "login" | "selector" | "loading" | "connected";

interface TogalUserProfile {
  user_id: string;
  organization_id: string;
}

interface TogalContextType {
  togalState: TogalState;
  setTogalState: (state: TogalState) => void;
  isTogalAuthenticated: boolean;
  setIsTogalAuthenticated: (auth: boolean) => void;
  togalSessionId: string | null;
  togalUserProfile: TogalUserProfile | null;
  togalProject: TogalProject | null;
  setTogalProject: (project: TogalProject | null) => void;
  togalSet: TogalSet | null;
  setTogalSet: (set: TogalSet | null) => void;
  togalClassifications: TogalClassification[] | null;
  togalAggregatedQtos: AggregatedQto[] | null;
  togalSetPageCount: number | null;
  togalInvitedUsers: TogalInvitedUserCard[] | null;
  togalLastUpdated: string | null;
  isRefreshing: boolean;
  connectTogalFromIds: (project: TogalProject, set: TogalSet) => Promise<void>;
  refreshClassifications: () => Promise<void>;
  initializeTogalState: () => Promise<void>;
  clearTogalSession: () => Promise<void>;
  /** When non-null, selector should adopt this project then clear via clearSelectorPrefill. */
  selectorPrefillProject: TogalProject | null;
  clearSelectorPrefill: () => void;
  returnToProjectSelectorWithProject: (project: TogalProject) => void;
  /** Clears project/set, workbook Togal cache, and shows the selector (no prefill). */
  disconnectTogalToSelector: () => Promise<void>;
}

const TogalContext = createContext<TogalContextType | undefined>(undefined);

export const TogalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { showBanner } = useBanner();
  const [togalState, setTogalState] = useState<TogalState>("login");
  const [isTogalAuthenticated, setIsTogalAuthenticated] = useState(false);
  const [togalProject, setTogalProject] = useState<TogalProject | null>(null);
  const [togalSet, setTogalSet] = useState<TogalSet | null>(null);
  const [togalClassifications, setTogalClassifications] = useState<TogalClassification[] | null>(null);
  const [togalAggregatedQtos, setTogalAggregatedQtos] = useState<AggregatedQto[] | null>(null);
  const [togalSetPageCount, setTogalSetPageCount] = useState<number | null>(null);
  const [togalInvitedUsers, setTogalInvitedUsers] = useState<TogalInvitedUserCard[] | null>(null);
  const [togalLastUpdated, setTogalLastUpdated] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectorPrefillProject, setSelectorPrefillProject] = useState<TogalProject | null>(null);

  const isRefreshingRef = useRef(false);
  const classificationsRef = useRef(togalClassifications);
  const aggregatedRef = useRef(togalAggregatedQtos);
  const pageCountRef = useRef(togalSetPageCount);
  const invitedUsersRef = useRef(togalInvitedUsers);

  classificationsRef.current = togalClassifications;
  aggregatedRef.current = togalAggregatedQtos;
  pageCountRef.current = togalSetPageCount;
  invitedUsersRef.current = togalInvitedUsers;

  const getSessionId = (): string | null => browserStore.get("togalSessionId");

  const getUserProfile = (): TogalUserProfile | null => {
    const raw = browserStore.get("togalUserProfile");
    if (!raw) return null;
    try {
      return JSON.parse(raw) as TogalUserProfile;
    } catch {
      return null;
    }
  };

  const getOrganizationId = (): string | null => {
    const orgId = browserStore.get("togalOrganizationId");
    if (orgId) return orgId;
    return getUserProfile()?.organization_id ?? null;
  };

  const mapInvitedUsers = (setDetails: TogalSetDetails): TogalInvitedUserCard[] => {
    const users = setDetails.permissions?.set?.users ?? {};
    return Object.entries(users).map(([id, user]) => {
      const displayName = user.name?.trim() || user.email;
      return {
        id,
        displayName,
        email: user.email,
      };
    });
  };

  const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> => {
    return new Promise<T>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(timeoutMessage));
      }, timeoutMs);

      promise
        .then((value) => {
          clearTimeout(timeoutId);
          resolve(value);
        })
        .catch((error) => {
          clearTimeout(timeoutId);
          reject(error);
        });
    });
  };

  const loadSetInvitedUsers = async (setId: string): Promise<{ invitedUsers: TogalInvitedUserCard[] }> => {
    const setResult = await fetchTogalSet(setId);

    if (!setResult.success && setResult.error instanceof UnauthorizedError) {
      throw setResult.error;
    }

    return {
      invitedUsers: setResult.success && setResult.data ? mapInvitedUsers(setResult.data) : [],
    };
  };

  const clearTogalSession = async () => {
    browserStore.remove("togalSessionId");
    browserStore.remove("togalOrganizationId");
    browserStore.remove("togalUserProfile");
    setIsTogalAuthenticated(false);
    setTogalProject(null);
    setTogalSet(null);
    setTogalClassifications(null);
    setTogalAggregatedQtos(null);
    setTogalSetPageCount(null);
    setTogalInvitedUsers(null);
    setTogalLastUpdated(null);
    setTogalState("login");
    await workbookCache.delete("togalProjectId");
    await workbookCache.delete("togalSetId");
  };

  const loadConnection = async (projectId: string, setId: string, orgId: string): Promise<boolean> => {
    const projectsResult = await fetchTogalProjects();
    if (!projectsResult.success || !projectsResult.data) return false;

    const project = projectsResult.data.find((p) => p.id === projectId);
    if (!project) return false;

    const effectiveOrgId = project.organization_id || orgId;
    const setsResult = await fetchTogalSets(projectId, effectiveOrgId);
    if (!setsResult.success || !setsResult.data) return false;

    const set = setsResult.data.rows.find((s) => s.id === setId);
    if (!set) return false;

    setTogalState("loading");

    const [aggregationResult, setDetailsResult] = await Promise.all([
      aggregateClassificationQuantities(effectiveOrgId, setId),
      fetchTogalSet(setId),
    ]);

    if (!aggregationResult.success || !aggregationResult.data) {
      if (!aggregationResult.success && aggregationResult.error instanceof UnauthorizedError) {
        await clearTogalSession();
        return true;
      }
      return false;
    }

    if (!setDetailsResult.success && setDetailsResult.error instanceof UnauthorizedError) {
      await clearTogalSession();
      return true;
    }

    const invitedUsers =
      setDetailsResult.success && setDetailsResult.data ? mapInvitedUsers(setDetailsResult.data) : [];

    setTogalProject(project);
    setTogalSet(set);
    setTogalClassifications(aggregationResult.data.classifications);
    setTogalAggregatedQtos(aggregationResult.data.aggregated);
    setTogalSetPageCount(aggregationResult.data.pageCount);
    setTogalInvitedUsers(invitedUsers);
    setTogalLastUpdated(new Date().toISOString());
    setTogalState("connected");
    return true;
  };

  const initializeTogalState = useCallback(async () => {
    const sessionId = getSessionId();
    if (!sessionId) {
      setTogalState("login");
      setIsTogalAuthenticated(false);
      return;
    }

    const isValid = await togalValidateSession();
    if (!isValid) {
      await clearTogalSession();
      return;
    }

    setIsTogalAuthenticated(true);

    const cachedProjectId = await workbookCache.get("togalProjectId");
    const cachedSetId = await workbookCache.get("togalSetId");

    if (!cachedProjectId || !cachedSetId) {
      setTogalState("selector");
      return;
    }

    const orgId = getOrganizationId();
    if (!orgId) {
      setTogalState("selector");
      return;
    }

    try {
      const connected = await loadConnection(cachedProjectId, cachedSetId, orgId);
      if (!connected) setTogalState("selector");
    } catch {
      setTogalState("selector");
    }
  }, []);

  const connectTogalFromIds = useCallback(
    async (project: TogalProject, set: TogalSet) => {
      const orgId = project.organization_id || getOrganizationId();
      if (!orgId) {
        setTogalState("selector");
        return;
      }

      setTogalState("loading");
      setTogalProject(project);
      setTogalSet(set);

      try {
        const [aggregationResult, setDetailsResult] = await Promise.all([
          aggregateClassificationQuantities(orgId, set.id),
          fetchTogalSet(set.id),
        ]);

        if (!aggregationResult.success || !aggregationResult.data) {
          if (!aggregationResult.success && aggregationResult.error instanceof UnauthorizedError) {
            await clearTogalSession();
            return;
          }
          showBanner(TOGAL_LOAD_ERROR, "error");
          setTogalState("selector");
          return;
        }

        if (!setDetailsResult.success && setDetailsResult.error instanceof UnauthorizedError) {
          await clearTogalSession();
          return;
        }

        const invitedUsers =
          setDetailsResult.success && setDetailsResult.data ? mapInvitedUsers(setDetailsResult.data) : [];

        setTogalClassifications(aggregationResult.data.classifications);
        setTogalAggregatedQtos(aggregationResult.data.aggregated);
        setTogalSetPageCount(aggregationResult.data.pageCount);
        setTogalInvitedUsers(invitedUsers);
        setTogalLastUpdated(new Date().toISOString());
        setTogalState("connected");
      } catch {
        showBanner(TOGAL_LOAD_ERROR, "error");
        setTogalState("selector");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showBanner]
  );

  const refreshClassifications = useCallback(async () => {
    if (isRefreshingRef.current) return;

    const orgId = togalProject?.organization_id || getOrganizationId();
    const setId = togalSet?.id;
    if (!orgId || !setId) return;

    isRefreshingRef.current = true;
    setIsRefreshing(true);

    const previousClassifications = classificationsRef.current;
    const previousAggregated = aggregatedRef.current;
    const previousSetPageCount = pageCountRef.current;
    const previousInvitedUsers = invitedUsersRef.current;

    setTogalClassifications(null);
    setTogalAggregatedQtos(null);
    setTogalSetPageCount(null);
    setTogalInvitedUsers(null);

    try {
      const refreshTimeoutMs = 30000;
      const [aggregationResult, usersResult] = await settleAll([
        withTimeout(
          aggregateClassificationQuantities(orgId, setId),
          refreshTimeoutMs,
          "Timed out fetching Togal classification data"
        ),
        withTimeout(loadSetInvitedUsers(setId), refreshTimeoutMs, "Timed out fetching Togal set metadata"),
      ]);

      if (usersResult.status === "rejected" && usersResult.reason instanceof UnauthorizedError) {
        await clearTogalSession();
        return;
      }

      let classificationsRefreshed = false;
      const usersRefreshed = usersResult.status === "fulfilled";

      if (aggregationResult.status === "fulfilled" && aggregationResult.value.success && aggregationResult.value.data) {
        setTogalClassifications(aggregationResult.value.data.classifications);
        setTogalAggregatedQtos(aggregationResult.value.data.aggregated);
        setTogalSetPageCount(aggregationResult.value.data.pageCount);
        setTogalLastUpdated(new Date().toISOString());
        classificationsRefreshed = true;
      } else if (
        aggregationResult.status === "fulfilled" &&
        !aggregationResult.value.success &&
        aggregationResult.value.error instanceof UnauthorizedError
      ) {
        await clearTogalSession();
        return;
      } else {
        setTogalClassifications(previousClassifications ?? []);
        setTogalAggregatedQtos(previousAggregated ?? []);
        setTogalSetPageCount(previousSetPageCount ?? 0);
      }

      if (usersRefreshed) {
        setTogalInvitedUsers(usersResult.value.invitedUsers);
      } else {
        setTogalInvitedUsers(previousInvitedUsers ?? []);
      }

      if (classificationsRefreshed && usersRefreshed) {
        showBanner(TOGAL_REFRESHED, "success");
      } else {
        showBanner(TOGAL_REFRESH_PARTIAL, "error");
      }
    } catch (err) {
      setTogalClassifications(previousClassifications ?? []);
      setTogalAggregatedQtos(previousAggregated ?? []);
      setTogalSetPageCount(previousSetPageCount ?? 0);
      setTogalInvitedUsers(previousInvitedUsers ?? []);
      showBanner(TOGAL_REFRESH_PARTIAL, "error");
    } finally {
      isRefreshingRef.current = false;
      setIsRefreshing(false);
    }
  }, [togalSet, showBanner]);

  const clearSelectorPrefill = useCallback(() => {
    setSelectorPrefillProject(null);
  }, []);

  const returnToProjectSelectorWithProject = useCallback((project: TogalProject) => {
    setSelectorPrefillProject(project);
    setTogalState("selector");
  }, []);

  const disconnectTogalToSelector = useCallback(async () => {
    clearSelectorPrefill();
    setTogalProject(null);
    setTogalSet(null);
    setTogalClassifications(null);
    setTogalAggregatedQtos(null);
    setTogalSetPageCount(null);
    setTogalInvitedUsers(null);
    setTogalLastUpdated(null);
    setTogalState("selector");
    await Promise.all([workbookCache.delete("togalProjectId"), workbookCache.delete("togalSetId")]);
  }, [clearSelectorPrefill]);

  useEffect(() => {
    initializeTogalState();
  }, [initializeTogalState]);

  return (
    <TogalContext.Provider
      value={{
        togalState,
        setTogalState,
        isTogalAuthenticated,
        setIsTogalAuthenticated,
        togalSessionId: getSessionId(),
        togalUserProfile: getUserProfile(),
        togalProject,
        setTogalProject,
        togalSet,
        setTogalSet,
        togalClassifications,
        togalAggregatedQtos,
        togalSetPageCount,
        togalInvitedUsers,
        togalLastUpdated,
        isRefreshing,
        connectTogalFromIds,
        refreshClassifications,
        initializeTogalState,
        clearTogalSession,
        selectorPrefillProject,
        clearSelectorPrefill,
        returnToProjectSelectorWithProject,
        disconnectTogalToSelector,
      }}
    >
      {children}
    </TogalContext.Provider>
  );
};

export const useTogal = () => {
  const context = useContext(TogalContext);
  if (!context) {
    throw new Error("useTogal must be used within a TogalProvider");
  }
  return context;
};
